import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { track } from "../lib/analytics";
import { wsUrl } from "../lib/api";
import ChatPanel from "../components/ChatPanel";
import LikeButton from "../components/LikeButton";

/**
 * Pre-live "waiting room" page.
 * Renders inside LiveRoom when the drop's status is `scheduled` (host hasn't
 * touched it yet) or `open` (host is prepping / preshow window). Gives the
 * member a rich pre-show experience: countdown → product teaser → price crash
 * ladder → live "N inside" social proof → calendar CTA.
 *
 * Auto-transition to the live experience is handled by the parent LiveRoom
 * component (polls /api/lives/{id} every 3s, re-renders when status flips).
 *
 * Arrival wave: opens the live-room WebSocket with the member's number so the
 * backend broadcasts a `member_arrival` event to everyone else in the room.
 * We render each arrival as a 3-second corner toast for zero-effort social
 * proof while the host preps.
 */
export default function PreLive({ live, memberNo, isAnonymous = false, onAddToCalendar }) {
  const nav = useNavigate();
  const scheduledIso = live?.scheduled_at;
  const target = useMemo(() => (scheduledIso ? new Date(scheduledIso).getTime() : Date.now()), [scheduledIso]);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, target - now);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);

  useEffect(() => {
    try { track("prelive_view", { live_id: live?.live_id, drop_no: live?.drop_no, status: live?.status }); } catch (_e) { /* noop */ }
    // Fire once per mount
  }, [live?.live_id, live?.drop_no, live?.status]);

  // --- Arrival wave (WebSocket) ------------------------------------------
  // Rolling queue of the last 3 arrivals shown as corner toasts. Each toast
  // auto-dismisses 3s after it lands.
  const [arrivals, setArrivals] = useState([]);
  const [liveRoomSize, setLiveRoomSize] = useState(null); // best-effort live headcount from WS
  const wsRef = useRef(null);

  useEffect(() => {
    if (!live?.live_id) return undefined;
    const qs = memberNo ? `?member_no=${encodeURIComponent(memberNo)}` : "";
    const ws = new WebSocket(wsUrl(`/api/ws/chat/${live.live_id}${qs}`));
    wsRef.current = ws;
    ws.onmessage = (ev) => {
      let msg;
      try { msg = JSON.parse(ev.data); } catch (_e) { return; }
      if (msg?.type === "member_arrival") {
        if (typeof msg.room_size === "number") setLiveRoomSize(msg.room_size);
        // Ignore our own arrival — the backend broadcasts to everyone in the
        // room including the newcomer. We'd rather see other members.
        if (memberNo && msg.member_no === Number(memberNo)) return;
        // Skip the host-observer sentinel (member_no=0) — HostLiveRoom
        // connects with that so it can track the waiting-room size without
        // spamming everyone else with a bogus "Member #000 arrived" toast.
        if (!msg.member_no || msg.member_no === 0) return;
        const id = `arr_${msg.ts || Date.now()}_${msg.member_no}`;
        setArrivals((prev) => {
          const next = [...prev.filter((a) => a.id !== id), { id, memberNo: msg.member_no, roomSize: msg.room_size }];
          return next.slice(-3);
        });
        setTimeout(() => {
          setArrivals((prev) => prev.filter((a) => a.id !== id));
        }, 3000);
        try { track("prelive_arrival", { live_id: live.live_id, member_no: msg.member_no, room_size: msg.room_size }); } catch (_e) { /* noop */ }
      }
    };
    return () => {
      try { ws.close(); } catch (_e) { /* noop */ }
      wsRef.current = null;
    };
  }, [live?.live_id, memberNo]);

  const drop = live?.drop_no ? String(live.drop_no).padStart(3, "0") : "001";
  const product = live?.product || {};
  const category = product.category || live?.category || "";
  const isPreshow = live?.status === "open";
  // Product reveal happens at 10 AM IST on the drop's day = scheduled_at − 11h.
  // After reveal we unblur the image (still show the room chrome — this is a
  // waiting/preview page, not a hijack of the live purchase flow).
  const revealAt = scheduledIso ? new Date(scheduledIso).getTime() - 11 * 3600 * 1000 : null;
  const isRevealed = !!(revealAt && Date.now() >= revealAt);
  // Prefer the live WebSocket headcount (updated on every arrival) over the
  // polled participants count so the pill feels real-time.
  const participants = liveRoomSize ?? (live?.participants || 0);
  const dateLabel = scheduledIso
    ? new Date(scheduledIso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: true })
    : "TBD";

  return (
    <div className="min-h-screen bg-[#F8F7F2] text-[#161616]" data-testid="pre-live-page">
      {/* Arrival wave — fixed corner toasts stacked bottom-up. Auto-dismiss after 3s. */}
      <div
        className="fixed z-50 flex flex-col items-end gap-2 pointer-events-none"
        style={{ top: "calc(env(safe-area-inset-top) + 80px)", right: "16px" }}
        data-testid="prelive-arrivals"
      >
        {arrivals.map((a) => (
          <div
            key={a.id}
            className="border-2 border-[#161616] bg-[#FFFFFF] shadow-[3px_3px_0_#264733] px-3 py-2 flex items-center gap-2 font-mono-ds text-[11px] uppercase tracking-[0.18em] animate-arrival-in"
            data-testid={`prelive-arrival-toast-${a.memberNo}`}
          >
            <span className="text-base leading-none">👋</span>
            <span className="text-[#161616]">
              Member <span className="font-display text-[#264733] font-bold tabular-nums">#{String(a.memberNo).padStart(3, "0")}</span> just arrived
            </span>
            {typeof a.roomSize === "number" && (
              <>
                <span className="text-[#E5E1D8]">·</span>
                <span className="text-[#6E6E6E] tabular-nums">{a.roomSize} inside</span>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Slim top bar */}
      <div className="px-4 md:px-10 py-4 border-b border-[#E5E1D8] flex items-center justify-between gap-3">
        <Link to={isAnonymous ? "/" : "/home"} className="font-mono-ds text-xs uppercase tracking-[0.3em] text-[#6E6E6E] hover:text-[#FF6A00]" data-testid="prelive-back">
          ← Back
        </Link>
        <div className="flex items-center gap-3">
          {live?.live_id && <LikeButton liveId={live.live_id} />}
          <div className="brand-tiny text-[#6E6E6E]" data-testid="prelive-drop-tag">
            Drop #{drop}{category ? ` · ${category}` : ""}
          </div>
        </div>
      </div>

      {/* Anonymous sign-in nudge (only for logged-out previewers). Non-blocking:
          the visitor can still browse the countdown, ladder, and social proof. */}
      {isAnonymous && (
        <div className="border-b border-[#161616] bg-[#B9E641] px-6 md:px-10 py-3" data-testid="prelive-anon-banner">
          <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-between gap-3 font-mono-ds text-[11px] uppercase tracking-[0.20em] text-[#161616]">
            <span>
              <span className="text-[#264733] font-bold">◆ Previewing as guest</span>
              <span className="opacity-40 mx-2">·</span>
              <span>Sign in to join the live drop and lock in the milestone price</span>
            </span>
            <button
              onClick={() => {
                try { track("prelive_anon_signin_click", { live_id: live?.live_id }); } catch (_e) { /* noop */ }
                const next = encodeURIComponent(`/live/${live?.live_id}`);
                nav(`/login?next=${next}`);
              }}
              className="border-2 border-[#161616] bg-[#161616] text-white px-4 h-9 hover:bg-[#FF6A00] hover:border-[#FF6A00] transition-colors"
              data-testid="prelive-anon-signin"
            >
              Sign in →
            </button>
          </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-4 md:px-10 py-8 md:py-14 grid md:grid-cols-12 gap-8 md:gap-12">
        {/* --------- LEFT: product silhouette --------- */}
        <div className="md:col-span-5">
          <div className="aspect-[4/3] border-2 border-[#161616] bg-gradient-to-br from-[#F0EDE4] to-[#E5E1D8] flex items-center justify-center relative overflow-hidden shadow-[6px_6px_0_#264733]" data-testid="prelive-product-teaser">
            {product.image_url ? (
              <img src={product.image_url} alt={product.name} className={`w-full h-full object-cover ${(isPreshow || isRevealed) ? "" : "opacity-40 blur-sm"}`} />
            ) : (
              <span className="font-display text-9xl text-[#161616]/12 leading-none select-none">?</span>
            )}
            <div className="absolute top-3 left-3 brand-pill-accent" data-testid="prelive-status-pill">
              {isPreshow ? "● Doors Opening" : "◇ Waiting Room"}
            </div>
            {participants > 0 && (
              <div className="absolute bottom-3 left-3 flex items-center gap-2 bg-[#161616]/85 text-white px-3 py-1.5 font-mono-ds text-[10px] uppercase tracking-[0.24em]" data-testid="prelive-viewers-badge">
                <span className="w-1.5 h-1.5 bg-[#B9E641] rounded-full animate-pulse" />
                {participants.toLocaleString("en-IN")} inside
              </div>
            )}
          </div>
        </div>

        {/* --------- RIGHT: countdown + info --------- */}
        <div className="md:col-span-7 flex flex-col">
          <div className="brand-tag mb-2">// The next drop</div>
          <h1 className="font-display uppercase text-4xl md:text-5xl leading-[0.95] tracking-tight" data-testid="prelive-title">
            {live?.title || "Tonight's Drop"}
          </h1>
          {product.name && (
            <div className="mt-3 font-mono-ds text-sm text-[#6E6E6E] uppercase tracking-widest" data-testid="prelive-product-name">
              {product.name}
            </div>
          )}
          <div className="mt-3 brand-tiny text-[#6E6E6E]">{dateLabel} IST</div>

          {/* Countdown */}
          <div className="mt-6" data-testid="prelive-countdown">
            <div className="brand-tiny text-[#6E6E6E] mb-2">{isPreshow ? "Doors opening now" : "Live starts in"}</div>
            <div className="flex flex-wrap gap-2 md:gap-3">
              {[["Days", days], ["Hours", hours], ["Minutes", mins], ["Seconds", secs]].map(([l, v], i) => (
                <div key={l} className={`border border-[#161616] bg-[#FFFFFF] px-3 md:px-4 py-2 md:py-3 min-w-[60px] md:min-w-[80px] flex flex-col items-center shadow-[2px_2px_0_#161616] ${i === 3 ? "text-[#FF6A00]" : ""}`}>
                  <span className="font-display text-2xl md:text-4xl leading-none tabular-nums">{String(v).padStart(2, "0")}</span>
                  <span className="font-mono-ds text-[9px] md:text-[10px] uppercase tracking-[0.24em] text-[#6E6E6E] mt-1">{l}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Social-proof strip */}
          <div className="mt-6 flex flex-wrap items-center gap-3 font-mono-ds text-[11px] uppercase tracking-[0.20em]" data-testid="prelive-socialproof">
            <span className="inline-flex items-center gap-2 border border-[#264733] bg-[#B9E641]/40 px-3 py-1.5 text-[#264733]">
              <span className={`w-1.5 h-1.5 rounded-full bg-[#264733] ${participants > 0 ? "animate-pulse" : ""}`} />
              <span className="tabular-nums font-bold">{participants.toLocaleString("en-IN")}</span>
              <span>member{participants === 1 ? "" : "s"} already here</span>
            </span>
            <span className="text-[#6E6E6E]">⏱ 2-minute drop · Price crashes with every join</span>
          </div>

          {/* CTA row */}
          <div className="mt-6 flex flex-wrap gap-3">
            {onAddToCalendar && (
              <button
                onClick={onAddToCalendar}
                className="btn-urgent h-12 px-6 flex items-center gap-2"
                data-testid="prelive-calendar-btn"
              >
                🗓 Add to Calendar
              </button>
            )}
            <Link to={isAnonymous ? "/#season" : "/home"} className="btn-outline-ns h-12 px-6 flex items-center" data-testid="prelive-see-schedule">
              See Full Schedule →
            </Link>
          </div>
        </div>
      </div>

      {/* --------- PRICE-CRASH LADDER --------- */}
      {Array.isArray(live?.milestones) && live.milestones.length > 0 && (
        <div className="border-t border-[#161616] bg-[#FFFFFF]" data-testid="prelive-ladder">
          <div className="max-w-5xl mx-auto px-4 md:px-10 py-10 md:py-14">
            <div className="brand-tag mb-2">// How prices crash</div>
            <h2 className="font-display text-2xl md:text-4xl uppercase tracking-tight leading-tight">
              The more members join, <span className="text-outline">the lower it drops.</span>
            </h2>
            <p className="mt-3 text-sm text-[#6E6E6E] max-w-2xl">
              Starting price is set the second the 2-minute live begins. As viewers cross each threshold, everyone in the room unlocks the next lower tier — locked in when the timer hits zero.
            </p>
            <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3">
              {live.milestones.map((m, i) => {
                const hit = participants >= (m.threshold ?? m.target ?? 0);
                return (
                  <div
                    key={i}
                    className={`border p-4 flex flex-col gap-1.5 shadow-[2px_2px_0_#161616] ${hit ? "border-[#264733] bg-[#B9E641]/40" : "border-[#161616] bg-[#F8F7F2]"}`}
                    data-testid={`prelive-tier-${i + 1}`}
                  >
                    <div className="font-mono-ds text-[10px] uppercase tracking-[0.24em] text-[#6E6E6E]">Tier {i + 1}</div>
                    <div className="font-display text-2xl leading-none tabular-nums text-[#161616]">₹{Number(m.price).toLocaleString("en-IN")}</div>
                    <div className="font-mono-ds text-[10px] uppercase tracking-widest text-[#264733] font-bold mt-1">
                      ≥ {m.threshold ?? m.target ?? 0} viewers
                    </div>
                    {hit && (
                      <div className="mt-1 font-mono-ds text-[9px] uppercase tracking-widest text-[#264733]">● Unlocked</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* --------- WAITING-ROOM CHAT (unlocks 5 min before scheduled_at) --------- */}
      {!isAnonymous && live?.live_id && (
        <div className="border-t border-[#161616] bg-[#F8F7F2]" data-testid="prelive-chat-section">
          <div className="max-w-5xl mx-auto px-4 md:px-10 py-10 md:py-14">
            <div className="brand-tag mb-2">// Say hi while you wait</div>
            <h2 className="font-display text-2xl md:text-4xl uppercase tracking-tight leading-tight">
              The room is <span className="text-outline">already talking.</span>
            </h2>
            <p className="mt-3 text-sm text-[#6E6E6E] max-w-2xl">
              Chat opens 5 minutes before the drop. Introduce yourself, greet the host, or claim a seat before the price crash begins.
            </p>
            <div className="mt-6 border-2 border-[#161616] bg-[#FFFFFF] h-[420px] md:h-[480px]" data-testid="prelive-chat">
              <ChatPanel
                liveId={live.live_id}
                liveStatus={live.status}
                userName={live.viewer_name || `Member #${String(memberNo || 0).padStart(3, "0")}`}
                isHost={false}
              />
            </div>
          </div>
        </div>
      )}

      {/* footer */}
      <div className="border-t border-[#161616] px-4 md:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-2 font-mono-ds text-[10px] uppercase tracking-[0.24em] text-[#6E6E6E]">
        <span>Drop Shop Live · One product a night · 9 PM IST</span>
        <span>Auto-refreshes when the host goes live</span>
      </div>
    </div>
  );
}
